import { icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { createStandardMenuItems, Menu } from "@msuite/ui/menu";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { type CellContext, RowActionsWrapper, useDataTableContext } from "@msuite/ui/table";
import { responseHandler } from "@msuite/ui/utils";
import type { TransportOrderListItem } from "@msuite/utils/types";
import { Fragment } from "react";
import { client } from "@/hono";
import { useCitadel } from "@/hooks/use-citadel";
import { useDisclosure } from "@msuite/ui/hooks";
import { TransportOrderEventsModal } from "@/modals/stock/transport-order-events-modal";

/**
 * What the office does to a transport leg: read who has had it, and call it off.
 *
 * There is no edit and no create. A leg is raised by the warehouse — the round's sign-off, or the
 * delivery before it — and it is finished by somebody scanning a label, so the only decision left
 * here is that a box is not going where it was going.
 *
 * The chain of custody is offered on **every** leg, closed ones included, and that is the point of
 * it: a delivered leg is exactly when somebody asks who carried the box, and the row cannot answer
 * because `pickedUpBy` is overwritten on a handover. Cancelling stays behind `update Stock` and an
 * open state; reading the history needs neither.
 */
export function RowActions({ cell }: CellContext<TransportOrderListItem, "actions">) {
	/** Hooks */
	const { t } = useTranslation();
	const asyncModal = useAsyncModal();
	const eventsModal = useDisclosure();

	/** Context */
	const { refetch } = useDataTableContext();

	/** Query */
	const { citadel } = useCitadel();

	/** Computed */
	// The same three-state guard `apps/web/src/components/citadel/can.tsx` documents: dropped only
	// on a definitive denial, because while the rules load the server keeps the last word.
	const stockUpdateIsDenied = !!citadel && citadel.cannot("update", "Stock");
	const isOpen = ["pending", "in-transit"].includes(cell.row.original.state);

	/** Functions */
	// Confirmed, because cancelling ends the chain rather than pausing it: no follow-up is raised,
	// and the box stays wherever it was last put down.
	async function handleOnCancel() {
		const isConfirmed = await asyncModal.confirm();
		if (!isConfirmed) return;
		const response = await client.stock["transport-orders"][":transportOrderId"].cancel.$patch({
			param: { transportOrderId: cell.row.original.id },
		});
		await responseHandler(response);
		await refetch?.();
	}

	/** Render */
	return (
		<Fragment>
			<RowActionsWrapper>
				<Menu
					items={createStandardMenuItems({
						items: [
							{
								label: t("stock.transport_event_history"),
								onClick: eventsModal.onOpen,
								icon: icon("Clock"),
							},
						],
						onDelete: handleOnCancel,
						onDeleteOptions: {
							label: t("stock.cancel_transport_order"),
							isDisabled: stockUpdateIsDenied || !isOpen,
						},
					})}
				>
					<Menu.Button />
				</Menu>
			</RowActionsWrapper>
			{asyncModal.isOpen && (
				<ConfirmModal
					message={t("stock.cancel_transport_order_confirm_message")}
					controller={asyncModal}
				/>
			)}
			{eventsModal.isOpen && (
				<TransportOrderEventsModal
					isOpen={eventsModal.isOpen}
					onClose={eventsModal.onClose}
					transportOrderId={cell.row.original.id}
				/>
			)}
		</Fragment>
	);
}
